import React, { useEffect, useMemo, useState } from 'react';
import { Check, LayoutGrid, Package, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useThemeMode } from '../../../../../../components/design-system/hooks';
import { Card } from './Card';
import { productV2Api, type FurnitureCategoryOption } from '../../../api/productV2Api';
import { PlacementScene } from './PlacementScene';

export type FurniturePlacement = 'floor' | 'wall';

interface FurnitureDetailsCardV3Props {
  /** Currently selected category slug (product-level). */
  category?: string;
  onCategoryChange: (slug: string) => void;
  /** Currently selected placement (visualization.model3d.arPlacement). */
  placement?: FurniturePlacement;
  onPlacementChange: (placement: FurniturePlacement) => void;
}

/** How many category cards to show inline before the "More" card. */
const PREVIEW_COUNT = 5;

// Build a slug → icon URL map from the generated webp assets. File names match
// the category slugs exactly (e.g. "tv stands.webp"). Falls back to a generic
// icon if a slug has no asset.
const CATEGORY_ICONS: Record<string, string> = {};
try {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const ctx = (require as any).context(
    '../../../../../../assets/icons/furniture-categories',
    false,
    /\.webp$/,
  );
  ctx.keys().forEach((key: string) => {
    const slug = key.replace(/^\.\//, '').replace(/\.webp$/, '');
    CATEGORY_ICONS[slug] = ctx(key);
  });
} catch {
  /* icons are optional — fall back to the generic icon below */
}

function getCategoryIcon(slug: string): React.ReactNode {
  const src = CATEGORY_ICONS[slug];
  if (src) {
    return <img src={src} alt="" className="size-[38px] object-contain" />;
  }
  return <Package className="size-8" />;
}

/** Section caption, shared by the two columns below. */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mb-2 block text-xs tracking-[0.04em] text-muted-foreground uppercase">{children}</p>
);

/** Minimal neutral selectable tile: icon + label with a quiet selected state. */
const SelectableCard: React.FC<{
  selected: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  dashed?: boolean;
  fill?: boolean;
}> = ({ selected, icon, label, onClick, dashed, fill }) => {
  return (
    <div
      onClick={onClick}
      className={cn(
        'relative flex cursor-pointer select-none flex-col items-center gap-1.5 border px-2 py-3.5 transition-colors',
        fill ? 'w-full' : 'w-[92px]',
        dashed ? 'border-dashed' : 'border-solid',
        selected
          ? 'border-foreground/40 bg-muted text-foreground'
          : 'border-border text-muted-foreground hover:bg-muted'
      )}
    >
      {selected && (
        <span className="absolute top-[7px] end-[7px] flex size-3.5 items-center justify-center rounded-full bg-muted-foreground">
          <Check className="size-2.5 text-background" />
        </span>
      )}
      <div className="flex h-10 items-center justify-center">{icon}</div>
      <span
        className={cn(
          'max-w-full truncate text-center text-[0.72rem] leading-tight',
          selected ? 'font-medium text-foreground' : 'text-muted-foreground'
        )}
      >
        {label}
      </span>
    </div>
  );
};

/** Segmented Floor / Wall toggle (single-choice, radio-like). */
const PlacementToggle: React.FC<{
  value: FurniturePlacement;
  onChange: (v: FurniturePlacement) => void;
  floorLabel: string;
  wallLabel: string;
}> = ({ value, onChange, floorLabel, wallLabel }) => {
  const options: Array<[FurniturePlacement, string]> = [
    ['floor', floorLabel],
    ['wall', wallLabel],
  ];
  return (
    <div className="inline-flex gap-[3px] border bg-muted p-[3px]">
      {options.map(([val, label]) => {
        const sel = value === val;
        return (
          <div
            key={val}
            onClick={() => onChange(val)}
            className={cn(
              'min-w-[72px] cursor-pointer px-5 py-1.5 text-center transition-colors',
              sel ? 'bg-background' : 'hover:bg-background/50'
            )}
          >
            <span
              className={cn(
                'text-[0.78rem]',
                sel ? 'font-medium text-foreground' : 'text-muted-foreground'
              )}
            >
              {label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Furniture-only editor section. Category is chosen from neutral icon cards — the
 * first few inline plus a "More" card that opens a clean searchable dialog of all
 * categories (synced with the YOLO taxonomy via pms-api-v2). Placement (floor /
 * wall) uses small animated scenes and maps to visualization.model3d.arPlacement.
 */
export const FurnitureDetailsCardV3: React.FC<FurnitureDetailsCardV3Props> = ({
  category,
  onCategoryChange,
  placement,
  onPlacementChange,
}) => {
  const { t } = useTranslation();
  // The placement preview is a real three.js scene: its materials need literal
  // colours, which CSS custom properties can't supply. This is the one place
  // the mode still has to be read in JS.
  const { isDarkMode } = useThemeMode();
  const sceneColors = {
    item: isDarkMode ? '#3C434D' : '#DDE1E6',
    floor: isDarkMode ? '#23272E' : '#F1F3F5',
    wall: isDarkMode ? '#2A2F37' : '#FAFBFC',
  };
  const [categories, setCategories] = useState<FurnitureCategoryOption[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let active = true;
    productV2Api
      .getFurnitureTaxonomy()
      .then((tax) => {
        if (active) setCategories(tax.categories);
      })
      .catch(() => {
        /* non-blocking: leave cards empty if the taxonomy can't load */
      });
    return () => {
      active = false;
    };
  }, []);

  // First few categories, but always keep the selected one visible.
  const previewCategories = useMemo(() => {
    const head = categories.slice(0, PREVIEW_COUNT);
    if (category && !head.some((c) => c.slug === category)) {
      const selected = categories.find((c) => c.slug === category);
      if (selected) return [selected, ...head].slice(0, PREVIEW_COUNT);
    }
    return head;
  }, [categories, category]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (c) => c.displayName.toLowerCase().includes(q) || c.slug.includes(q),
    );
  }, [categories, search]);

  return (
    <Card title={t('PAGES.NEW_PRODUCT.V3.FURNITURE.TITLE', 'Furniture details')}>
      <div className="flex flex-wrap items-start gap-6">
        {/* Category */}
        <div className="min-w-[240px] flex-1">
          <SectionLabel>
            {t('PAGES.NEW_PRODUCT.V3.FURNITURE.CATEGORY', 'Category')}
          </SectionLabel>
          <div className="flex flex-wrap gap-2.5">
            {previewCategories.map((c) => (
              <SelectableCard
                key={c.slug}
                selected={category === c.slug}
                icon={getCategoryIcon(c.slug)}
                label={c.displayName}
                onClick={() => onCategoryChange(c.slug)}
              />
            ))}
            <SelectableCard
              dashed
              selected={false}
              icon={<LayoutGrid className="size-7" />}
              label={t('PAGES.NEW_PRODUCT.V3.FURNITURE.MORE', 'More')}
              onClick={() => {
                setSearch('');
                setDialogOpen(true);
              }}
            />
          </div>
        </div>

        {/* Placement */}
        <div className="min-w-[240px] shrink-0 grow-0 basis-[288px]">
          <SectionLabel>
            {t('PAGES.NEW_PRODUCT.V3.FURNITURE.PLACEMENT', 'Placement')}
          </SectionLabel>
          <div className="overflow-hidden rounded-xl border bg-card">
            <div className="relative h-[168px] bg-gradient-to-b from-card to-muted">
              <PlacementScene placement={placement ?? 'floor'} colors={sceneColors} />
            </div>
            <div className="flex justify-center border-t py-3">
              <PlacementToggle
                value={placement ?? 'floor'}
                onChange={onPlacementChange}
                floorLabel={t('PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.FLOOR', 'Floor')}
                wallLabel={t('PAGES.NEW_PRODUCT.GENERAL_INFO.FIELDS.WALLS', 'Wall')}
              />
            </div>
          </div>
        </div>
      </div>

      {/* All-categories searchable dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="gap-0 p-0 sm:max-w-md">
          {/* Header */}
          <DialogHeader className="px-5 pt-5 pb-3">
            <DialogTitle>
              {t('PAGES.NEW_PRODUCT.V3.FURNITURE.SELECT_CATEGORY', 'Select a category')}
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="px-5 pb-3">
            <div className="relative">
              <Search className="absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                placeholder={t('PAGES.NEW_PRODUCT.V3.FURNITURE.SEARCH', 'Search categories')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-8"
              />
            </div>
          </div>

          {/* Grid */}
          <div className="grid max-h-80 grid-cols-3 gap-2.5 overflow-y-auto px-5 pt-1 pb-5">
            {filteredCategories.map((c) => (
              <SelectableCard
                key={c.slug}
                fill
                selected={category === c.slug}
                icon={getCategoryIcon(c.slug)}
                label={c.displayName}
                onClick={() => {
                  onCategoryChange(c.slug);
                  setDialogOpen(false);
                }}
              />
            ))}
            {filteredCategories.length === 0 && (
              <p className="col-span-full py-6 text-center text-xs text-muted-foreground">
                {t('PAGES.NEW_PRODUCT.V3.FURNITURE.NO_RESULTS', 'No categories found')}
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default FurnitureDetailsCardV3;
